/* ============================================================
   GH-1133 S5 — PreviewResumeHost (design §1.1 #11, §2.0 C8, §3.9 item 3).
   Mounted after RequireActiveBilling allows access and the shared post-trial
   gate resolves any pending host return/onboarding invitation. After checkout
   the preview provider is gone, so this cannot live inside it. It does nothing (no request, renders
   null) unless the trial wall wrote sessionStorage dathent.freePreview.resume
   on Continue, which only the preview's wall does. It does not depend on the
   ?checkout param surviving.

   When the account is now trialing/active: a toast, then a bar on the post
   the user wanted — [Publish now]/[Schedule] (draft with an image),
   [Add image]/[Publish as text-only] (text draft: the image-every-post rule
   holds image-less posts otherwise), [Connect a channel] (none connected) or
   [Create your next post]. The bar never publishes by itself.

   Trial wording (toastTrialOn / resumePublish / resumeTextDraft) only when a
   trial actually runs — resumeTrialOn: accessState 'trialing' and the plan
   stored on the intent has a trial. The wall also sells the €3.99 Scheduler,
   billed today with no trial ('active'); that, and an AI plan bought without
   a trial, get toastPlanOn / resumePlanPublish / resumePlanTextDraft
   (§2.1 principle 5, §2.7).
   ============================================================ */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api/client.js';
import { notify } from '../../lib/notifications.js';
import { clearResumeIntent, readResumeIntent, resumeTrialOn, resumeVariant } from '../../lib/freePreview.js';
import { trackPreview, trackPreviewOnce } from '../../lib/freePreviewEvents.js';

function sessionStore() {
  try { return window.sessionStorage; } catch { return null; }
}

function unwrap(res) {
  return res?.data ?? res;
}

// Paid-like states that end the wait. Whether a TRIAL runs is a separate
// question (resumeTrialOn): a Scheduler subscription is 'active', not trialing.
const PAID_STATES = new Set(['trialing', 'active']);
export const RESUME_READ_ATTEMPTS = 5;
const RESUME_READ_DELAY_MS = 2500;

export default function PreviewResumeHost() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bar, setBar] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const storage = sessionStore();
    const intent = readResumeIntent(storage);
    if (!intent) return undefined;
    let cancelled = false;
    (async () => {
      // RequireActiveBilling lets a paid user in optimistically while the
      // Stripe webhook may still be landing, so re-read a few times.
      let paid = false;
      let trialOn = false;
      for (let attempt = 0; attempt < RESUME_READ_ATTEMPTS && !cancelled; attempt += 1) {
        try {
          api.invalidateCache?.(['/api/billing/entitlements']);
          const ent = unwrap(await api.billing.getEntitlements());
          paid = PAID_STATES.has(ent?.accessState) || PAID_STATES.has(ent?.billingStatus);
          trialOn = paid && resumeTrialOn(ent, intent.plan);
        } catch { paid = false; trialOn = false; }
        if (paid || cancelled || attempt === RESUME_READ_ATTEMPTS - 1) break;
        await new Promise((resolve) => setTimeout(resolve, RESUME_READ_DELAY_MS));
      }
      if (cancelled || !paid) return; // not paid (yet): keep the intent for the next mount
      clearResumeIntent(storage);
      trackPreviewOnce('preview_card_added', { source: intent.source });
      notify({ level: 'success', title: t(trialOn ? 'freePreview.toastTrialOn' : 'freePreview.toastPlanOn') });
      let hasPost = false;
      let hasImage = false;
      let hasChannel = true;
      if (intent.postId) {
        try {
          const raw = unwrap(await api.content.get(intent.postId, { fresh: true }));
          const item = raw?.contentItem || raw || {};
          const version = raw?.activeVersion || null;
          hasPost = item.id === intent.postId && !item.archivedAt;
          hasImage = Boolean(version?.imageUrl || raw?.mediaPreview?.url || (Array.isArray(raw?.mediaUrls) && raw.mediaUrls.length));
        } catch { hasPost = false; }
      }
      try {
        const accounts = unwrap(await api.socialAccounts.list());
        hasChannel = Array.isArray(accounts?.accounts) ? accounts.accounts.length > 0 : true;
      } catch { hasChannel = true; }
      if (cancelled) return;
      const variant = resumeVariant({ hasPost, hasImage, hasChannel });
      setBar({ source: intent.source, postId: intent.postId, variant, trialOn });
      trackPreview('preview_resume_shown', { source: intent.source, variant });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!bar) return null;

  const postPath = (suffix = '') => `/app/post/${encodeURIComponent(bar.postId || '')}${suffix}`;
  const act = (action, go) => () => {
    trackPreview('preview_resume_clicked', { source: bar.source, action });
    setBar(null);
    go?.();
  };
  const allowTextOnly = async () => {
    if (busy) return;
    setBusy(true);
    trackPreview('preview_resume_clicked', { source: bar.source, action: 'text_only' });
    try {
      // Explicit consent through the existing TASK-074 release; it does not publish.
      await api.content.postAllowTextOnly(bar.postId);
      setBar(null);
      navigate(postPath('/edit?tab=schedule'));
    } catch (err) {
      notify({ level: 'error', title: t('postPreview.heldReleaseFailed'), error: err });
    } finally {
      setBusy(false);
    }
  };

  const headline = t(bar.trialOn ? 'freePreview.toastTrialOn' : 'freePreview.toastPlanOn');
  let text;
  let actions;
  if (bar.variant === 'publish') {
    text = t(bar.trialOn ? 'freePreview.resumePublish' : 'freePreview.resumePlanPublish');
    actions = (
      <>
        <button type="button" className="btn btn-primary btn-sm" onClick={act('publish', () => navigate(postPath('/edit?tab=schedule')))}>{t('postPreview.publishNow')}</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={act('schedule', () => navigate(postPath('/edit?tab=schedule')))}>{t('postPreview.scheduleThisPost')}</button>
      </>
    );
  } else if (bar.variant === 'text') {
    text = t(bar.trialOn ? 'freePreview.resumeTextDraft' : 'freePreview.resumePlanTextDraft');
    actions = (
      <>
        <button type="button" className="btn btn-primary btn-sm" onClick={act('add_image', () => navigate(postPath('/edit?tab=media')))}>{t('freePreview.resumeAddImage')}</button>
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={allowTextOnly}>{t('freePreview.resumeTextOnly')}</button>
      </>
    );
  } else if (bar.variant === 'connect') {
    text = headline;
    actions = <button type="button" className="btn btn-primary btn-sm" onClick={act('connect', () => navigate('/app/settings?tab=accounts'))}>{t('freePreview.resumeConnect')}</button>;
  } else {
    text = headline;
    actions = <button type="button" className="btn btn-primary btn-sm" onClick={act('create', () => navigate('/app/create'))}>{t('freePreview.resumeCreate')}</button>;
  }

  return (
    <div className="fp-resume" role="region" aria-label={headline} data-variant={bar.variant}>
      <span className="fp-resume-t">{text}</span>
      <div className="fp-resume-a">
        {actions}
        <button type="button" className="btn btn-ghost btn-sm" aria-label={t('common.close')} onClick={act('dismiss')}>×</button>
      </div>
    </div>
  );
}
